import * as React from "react"
import { ChangeEvent, HTMLInputTypeAttribute } from "react";
import styled from "styled-components";
import cn from "classnames";

import { mixins, themeValues } from "../../theme";
import { SelectSizes } from "./types";
import { IconTypes, IconAlignTypes } from "../../types";
import { Icon } from "../../Icon/Icon";

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  &.fullWidth {
    width: 100%;
  }
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  
  .input-label {
    font-size: ${themeValues.fontSize.normal};
    color: ${themeValues.colors.dark100};    
    line-height: 18px;
    margin-bottom: 4px;
    display: block;
  }
  .input-error, .input-description {
    font-size: ${themeValues.fontSize.small};
    line-height: 18px;
    display: block;
    margin-top: 4px;
  }
  .input-description {
    color: ${themeValues.colors.dark64};
  }
  .input-error {
    color: ${themeValues.colors.red100};
  }
  &.disabled {
    .input-label, .input-description {
      color: ${themeValues.colors.dark48};
    }
    i {
        color: ${themeValues.colors.dark48};
      }
  }

  .input-wrapper {
      i {
        position: absolute;
      }
    }

  &.large {
    .input-wrapper {
      i {
        top: 13px;
        left: 14px;
      }
      .chevron {
        left: auto;
        right: 12px;
        top: 17px;
      }
    }
  }
  &.medium {
    .input-wrapper {
      i {
        top: 11px;
        left: 11px;
      }
      .chevron {
        left: auto;
        right: 12px;
        top: 15px;
      }
    }
  }
  &.small {
    .input-wrapper {
      i {
        top: 8px;
        left: 11px;
      }
      .chevron {
        left: auto;
        right: 12px;
        top: 11px;
      }
    }
  }
`;
Wrapper.displayName = "SelectWrapper";

const SelectComponent = styled.select`
  height: 40px;
  line-height: normal;
  padding: 4px 48px 4px 8px;
  transition: 0.2s;

  border: 1px solid ${themeValues.colors.dark16};
  font-size: ${themeValues.fontSize.normal};
  color: ${themeValues.colors.dark100};
  appearance:none;


  ${mixins.rounded};

  &.error {
    border-color: ${themeValues.colors.red100};
  }

  &::placeholder {
    color:  ${themeValues.colors.dark64};
  }

  &.small {
    height: ${themeValues.inputSize.small};
    line-height: normal;

    &::placeholder {
      font-size: ${themeValues.fontSize.small};
    }
  }

  &.medium, &.large {
    font-size: ${themeValues.fontSize.large};
    &::placeholder {
      font-size: ${themeValues.fontSize.large};
    }
  }

  &.small {
    &.icon {
      padding-left: 32px;
    }
  }

  &.medium {
    height: ${themeValues.inputSize.medium};

    &.icon {
      padding-left: 32px;
    }
  }
  
  &.large {
    height: ${themeValues.inputSize.large};

    &.icon {
      padding-left: 42px;
    }
  }

  &.fullWidth {
    width: 100%;
  }

  &[disabled] {
    background-color: ${themeValues.colors.dark8};
    color: ${themeValues.colors.dark48};
    &::placeholder {
      color: ${themeValues.colors.dark48};
    }
  }

  &:not([disabled]) {
    &:hover,
    &:focus,
    &:active,
    &.hover {
      &:not(.error) {
        border-color: ${themeValues.colors.blue100};
      }
      outline: none;
    }
  }
`;
SelectComponent.displayName = "Select";

export interface ISelectItem {
  value: string;
  name: string;
}

interface ISelectProps {
  fullWidth?: boolean;
  label?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  error?: string;
  description?: string;
  inputClassName?: string;
  items: ISelectItem[];
  size?: SelectSizes;
  value: string;
  icon?: IconTypes | JSX.Element;
  iconAlign?: IconAlignTypes;
  type: HTMLInputTypeAttribute,
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

// TODO: TS issue with 'size'
export function Select({ value, type = "text", label, error, id, name, description, placeholder, onChange, required, disabled, fullWidth, size = "medium", autoFocus, inputClassName, icon, items }: ISelectProps): JSX.Element {
  const iconSize = size == "small" ? "xsmall" : (size == "medium" ? "small" : "medium")
  const chevronSize = size == "small" ? 6 : (size == "medium" ? 7 : 8)

  return <Wrapper className={cn(size, { "disabled": disabled })}>
    {label && <label className="input-label" htmlFor={id || name}>{label}</label>}
    <SelectWrapper className={cn("input-wrapper", { fullWidth: fullWidth })}>
      {/*// @ts-ignore TODO:*/}
      <Icon size={iconSize} glyph={icon} />
      <SelectComponent
        id={id}
        name={name}
        value={value}
        type={type}
        // @ts-ignore TODO:
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={cn(size, inputClassName, {
          "fullWidth": fullWidth,
          "error": !!error,
          "icon": !!icon,
        })}
      >
        {items && items.map(({ value, name }) => <option value={value}>{name}</option>)}
      </SelectComponent>
      {/*// @ts-ignore TODO:*/}
      <Icon className="chevron" size={chevronSize} glyph="icon-chevron-down" />
    </SelectWrapper>
    {error && <span className="input-error">{error}</span>}
    {description && <span className="input-description">{description}</span>}
  </Wrapper>;
}


